//AcceptInvitePage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { api } from '../../api';
import Header from '../header/header';
import SignUp from '../auth/SignUp';
import { performLogout  } from '../logout';
import { notifyError, notifySuccess } from '../Notification';
import './AcceptInvite.css';

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token] = useState(searchParams.get('token'));
  
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [inviteMode, setInviteMode] = useState(null); // 'new-user', 'existing-user', 'logged-in'
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    if (!token) {
      setTokenError('Invalid invitation link - No token provided');
      setLoading(false);
      return;
    }

    checkAuthAndFetchInvite();
  }, [token]);

  const checkAuthAndFetchInvite = async () => {
    try {
      // First, verify the invitation token (public endpoint)
      const inviteResponse = await api.get(`/invite/verify/${token}`);
      if (!inviteResponse.data.success) {
        setTokenError('Invalid or expired invitation');
        setLoading(false);
        return;
      }
      
      setInviteData(inviteResponse.data.data);
      const inviteEmail = inviteResponse.data.data.email;

      // Check if user is currently authenticated
      try {
        const authSession = await fetchAuthSession();
        if (authSession && authSession.tokens && authSession.tokens.accessToken) {
          // User is logged in - get their email
          const currentUserEmail = authSession.tokens.idToken?.payload?.email || 
                                  authSession.tokens.accessToken?.payload?.username;
          
          setCurrentUser({ 
            email: currentUserEmail,
            accessToken: authSession.tokens.accessToken
          });
          
          if (currentUserEmail === inviteEmail) {
            // Same user - can accept invitation
            setInviteMode('logged-in-same-user');
          } else {
            // Different user - need to decide what to do
            setInviteMode('logged-in-different-user');
          }
        } else {
          // User not authenticated
          setInviteMode('not-authenticated');
        }
      } catch (authError) {
        console.log('User not authenticated:', authError);
        setInviteMode('not-authenticated');
      }

    } catch (error) {
      console.error('Error fetching invitation:', error);
      setTokenError(error.response?.data?.message || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptForLoggedInUser = async () => {
    setSubmitting(true);
    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession.tokens?.accessToken;
      
      if (!accessToken) {
        throw new Error('No valid session found - please log in again');
      }

      console.log('Accepting invitation for logged-in user:', currentUser.email);
      
      // Use the protected endpoint for authenticated users
      const response = await api.post('/invite/accept-existing', 
        { token: token },
        {
          headers: {
            'Authorization': `Bearer ${accessToken.toString()}`
          }
        }
      );

      if (response.data.success) {
        notifySuccess(response.data.message);
        // Give a moment for the success notification to show
        setTimeout(() => {
          navigate('/Admin', { replace: true });
        }, 1000);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        notifyError('Your session has expired. Please log in again.');
        // Force logout and retry
        await performLogout();
        navigate('/login', { 
          state: { 
            message: 'Please log in to accept the invitation',
            redirectTo: `/accept-invite?token=${token}` 
          }
        });
      } else {
        notifyError(error.response?.data?.message || 'Failed to accept invitation');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAccount = async (userData) => {
    setSubmitting(true);
    try {
      console.log('Creating account for new user:', userData);
      
      // Use public endpoint for new user creation
      const response = await api.post('/invite/accept-new-user', {
        token: token,
        firstName: userData.firstName,
        lastName: userData.lastName,
        password: userData.password,
        email: inviteData.email
      });

      if (response.data.success) {
        notifySuccess('Account created successfully! Please log in to continue.');
        // Redirect to login with pre-filled email
        setTimeout(() => {
          navigate('/login', {
            state: {
              prefilledEmail: inviteData.email,
              message: 'Account created! Please log in to access your new business.'
            }
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating account:', error);
      notifyError(error.response?.data?.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSwitchAccount = async () => {
    try {
      // Store the invitation token for after login
      localStorage.setItem('pendingInviteToken', token);
      localStorage.setItem('inviteTargetEmail', inviteData.email);
      
      // Properly sign out and clear all data
      await performLogout();
      
      // Navigate to login with context
      navigate('/login', {
        state: {
          message: `Please log in as ${inviteData.email} to accept this invitation`,
          prefilledEmail: inviteData.email
        }
      });
    } catch (error) {
      console.error('Error during logout:', error);
      notifyError('Error switching accounts. Please try again.');
    }
  };

  const handleContinueWithCurrentAccount = () => {
    // Allow continuing with different account (useful for testing or if user controls both emails)
    setInviteMode('logged-in-same-user');
  };

  const handleRedirectToLogin = () => {
    // Store the invitation token for after login
    localStorage.setItem('pendingInviteToken', token);
    localStorage.setItem('inviteTargetEmail', inviteData.email);
    
    navigate('/login', {
      state: {
        prefilledEmail: inviteData.email,
        message: `Please log in as ${inviteData.email} to accept this invitation`
      }
    });
  };

  // Check for pending invite on component mount (for users coming back from login)
  useEffect(() => {
    const checkPendingInvite = async () => {
      const pendingToken = localStorage.getItem('pendingInviteToken');
      const targetEmail = localStorage.getItem('inviteTargetEmail');
      
      if (pendingToken && targetEmail && !token) {
        // User came back from login, process the pending invitation
        const currentToken = searchParams.get('token') || pendingToken;
        
        // Clean up localStorage
        localStorage.removeItem('pendingInviteToken');
        localStorage.removeItem('inviteTargetEmail');
        
        // Update URL and reload with token
        navigate(`/accept-invite?token=${currentToken}`, { replace: true });
      }
    };

    if (currentUser && !token) {
      checkPendingInvite();
    }
  }, [currentUser, token, searchParams, navigate]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="accept-invite-container">
          <div className="accept-invite-loading">
            <div className="accept-invite-spinner-large"></div>
            <p>Loading invitation details...</p>
          </div>
        </div>
      </>
    );
  }

  if (tokenError) {
    return (
      <>
        <Header />
        <div className="accept-invite-container">
          <div className="accept-invite-card">
            <div className="accept-invite-error-icon">
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h1 className="accept-invite-error-title">Invalid Invitation</h1>
            <p className="accept-invite-error-message">{tokenError}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                onClick={() => navigate('/login')}
                className="accept-invite-btn accept-invite-btn-primary"
              >
                Go to Login
              </button>
              <button 
                onClick={() => navigate('/')}
                className="accept-invite-btn"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Different renders based on invite mode
  const renderInviteContent = () => {
    switch (inviteMode) {
      case 'logged-in-same-user':
        return (
          <div className="accept-invite-card">
            <div className="accept-invite-header">
              <div className="accept-invite-header-icon">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h1 className="accept-invite-title">Accept Invitation</h1>
              <p className="accept-invite-subtitle">
                You've been invited to join {inviteData?.businessName}
              </p>
            </div>

            <div className="accept-invite-details">
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Your Email:</span>
                <span className="accept-invite-detail-value">{currentUser?.email}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">New Role:</span>
                <span className="accept-invite-role-badge">{inviteData?.role}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Business:</span>
                <span className="accept-invite-detail-value">{inviteData?.businessName}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Invited by:</span>
                <span className="accept-invite-detail-value">{inviteData?.invitedBy}</span>
              </div>
            </div>

            <div className="accept-invite-form-section">
              <p>This will add {inviteData?.businessName} to your existing account with the role of {inviteData?.role}.</p>
              
              <button
                onClick={handleAcceptForLoggedInUser}
                disabled={submitting}
                className="accept-invite-btn accept-invite-btn-primary accept-invite-btn-full"
              >
                {submitting ? (
                  <>
                    <div className="accept-invite-spinner"></div>
                    <span>Adding Access...</span>
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4"/>
                    </svg>
                    <span>Accept Invitation</span>
                  </>
                )}
              </button>
            </div>
          </div>
        );

      case 'logged-in-different-user':
        return (
          <div className="accept-invite-card">
            <div className="accept-invite-header">
              <div className="accept-invite-header-icon" style={{ color: '#f59e0b' }}>
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01"/>
                </svg>
              </div>
              <h1 className="accept-invite-title">Account Mismatch</h1>
              <p className="accept-invite-subtitle">
                This invitation is for a different email address
              </p>
            </div>

            <div className="accept-invite-details">
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Invitation for:</span>
                <span className="accept-invite-detail-value">{inviteData?.email}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Currently logged in as:</span>
                <span className="accept-invite-detail-value">{currentUser?.email}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Role:</span>
                <span className="accept-invite-role-badge">{inviteData?.role}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Business:</span>
                <span className="accept-invite-detail-value">{inviteData?.businessName}</span>
              </div>
            </div>

            <div className="accept-invite-form-section">
              <p>You need to be logged in as <strong>{inviteData?.email}</strong> to accept this invitation.</p>
              
              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                <button
                  onClick={handleSwitchAccount}
                  className="accept-invite-btn accept-invite-btn-primary"
                >
                  Switch to {inviteData?.email}
                </button>
                
                <button
                  onClick={handleContinueWithCurrentAccount}
                  className="accept-invite-btn"
                  style={{ background: '#6b7280', color: 'white' }}
                  title="Use this if you have access to both email accounts"
                >
                  Continue anyway (Advanced)
                </button>
              </div>
              
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px' }}>
                Only use "Continue anyway" if you have access to both email accounts and want to accept this invitation with your current account.
              </p>
            </div>
          </div>
        );

      case 'not-authenticated':
        return (
          <div className="accept-invite-card">
            <div className="accept-invite-header">
              <div className="accept-invite-header-icon">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
              <h1 className="accept-invite-title">Join {inviteData?.businessName}</h1>
              <p className="accept-invite-subtitle">
                You've been invited as a {inviteData?.role}
              </p>
            </div>

            <div className="accept-invite-details">
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Your Email:</span>
                <span className="accept-invite-detail-value">{inviteData?.email}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Role:</span>
                <span className="accept-invite-role-badge">{inviteData?.role}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Business:</span>
                <span className="accept-invite-detail-value">{inviteData?.businessName}</span>
              </div>
              <div className="accept-invite-detail-row">
                <span className="accept-invite-detail-label">Invited by:</span>
                <span className="accept-invite-detail-value">{inviteData?.invitedBy}</span>
              </div>
            </div>

            <div className="accept-invite-form-section">
              <p>To accept this invitation, please sign in or create an account with <strong>{inviteData?.email}</strong></p>
              
              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                <button
                  onClick={handleRedirectToLogin}
                  className="accept-invite-btn accept-invite-btn-primary"
                >
                  Sign In as {inviteData?.email}
                </button>
                
                <div style={{ textAlign: 'center', padding: '12px 0', color: '#6b7280' }}>
                  or
                </div>
                
                <details className="signup-details">
                  <summary className="signup-summary">Create New Account</summary>
                  <div className="signup-container" style={{ marginTop: '16px' }}>
                    <SignUp 
                      onClose={() => {}} 
                      prefilledEmail={inviteData?.email}
                      onSignUpComplete={handleCreateAccount}
                      submitText="Create Account & Accept Invitation"
                    />
                  </div>
                </details>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="accept-invite-card">
            <div className="accept-invite-loading">
              <div className="accept-invite-spinner-large"></div>
              <p>Determining authentication status...</p>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Header />
      <div className="accept-invite-container">
        {renderInviteContent()}
      </div>
    </>
  );
};

export default AcceptInvitePage;